import { useState } from "react";
import { format, parse } from "date-fns";
import { MapPin, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { TimeSelect } from "@/components/TimeSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { tourHoursFromPickup, plusOneHour, dayTourHours } from "@/lib/fields";

// Hourly-rate options: $400–$950 in $50 steps.
const RATE_OPTIONS = Array.from({ length: 12 }, (_, i) => 400 + i * 50);

const fmtDate = (mdY) => {
  try {
    const d = parse(String(mdY), "MM/dd/yyyy", new Date());
    return isNaN(d.getTime()) ? mdY : format(d, "EEE, MMM d");
  } catch {
    return mdY;
  }
};

export const DayShiftEditor = ({ dayShifts = [], onChange, testid = "day-shifts", loggedDates = [], onToggleDay }) => {
  const [busyDate, setBusyDate] = useState(null);
  const multiDay = dayShifts.length >= 2;
  const normDate = (v) => { try { const d = parse(String(v), "MM/dd/yyyy", new Date()); return isNaN(d.getTime()) ? String(v) : format(d, "yyyy-MM-dd"); } catch { return String(v); } };
  const loggedSet = new Set((loggedDates || []).map(normDate));
  const toggleDay = async (date) => {
    if (!onToggleDay) return;
    setBusyDate(date);
    try { await onToggleDay(date); } finally { setBusyDate(null); }
  };
  if (!dayShifts.length) {
    return <p className="text-sm text-zinc-400" data-testid={`${testid}-empty`}>Pick tour date(s) above to set shift times for each day.</p>;
  }
  const set = (i, patch) => onChange(dayShifts.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  // The tour timer runs from PICKUP → (shift end − 1h). Changing start/end/pickup recomputes
  // tour hours from the pickup; typing in the Tour Hours field is a manual override.
  const setStart = (i, v) => { const mt = v ? plusOneHour(v) : ""; set(i, { start: v, meet_time: mt, hours: tourHoursFromPickup(mt, dayShifts[i].end), hours_manual: false }); };
  const setEnd = (i, v) => { const d = dayShifts[i]; const mt = d.meet_time || (d.start ? plusOneHour(d.start) : ""); set(i, { end: v, hours: tourHoursFromPickup(mt, v), hours_manual: false }); };
  const setPickup = (i, v) => { const d = dayShifts[i]; set(i, { meet_time: v, hours: tourHoursFromPickup(v, d.end), hours_manual: false }); };

  return (
    <div className="space-y-2.5" data-testid={testid}>
      {dayShifts.map((d, i) => (
        <div key={d.date + i} className="rounded-lg border border-zinc-800 bg-zinc-950/40 px-3 py-2.5" data-testid={`${testid}-row-${i}`}>
          <div className="mb-2 text-sm font-500 text-zinc-200">{fmtDate(d.date)}</div>

          <div className="relative mb-2">
            <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#E0575B]" />
            <Input
              value={d.meet_location ?? ""}
              onChange={(e) => set(i, { meet_location: e.target.value })}
              placeholder="Meeting location"
              className="h-9 border-zinc-800 bg-zinc-900 pl-8 text-sm focus-visible:ring-zinc-500"
              data-testid={`${testid}-location-${i}`}
            />
          </div>

          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Label className="mb-1 block text-[10px] uppercase tracking-wide text-zinc-500">Shift</Label>
              <div className="flex items-center gap-1.5">
                <TimeSelect value={d.start} onChange={(v) => setStart(i, v)} testid={`${testid}-start-${i}`} className="min-w-0 flex-1 border-zinc-800 bg-zinc-900 focus:ring-zinc-500" />
                <span className="shrink-0 text-zinc-500">–</span>
                <TimeSelect value={d.end} onChange={(v) => setEnd(i, v)} testid={`${testid}-end-${i}`} className="min-w-0 flex-1 border-zinc-800 bg-zinc-900 focus:ring-zinc-500" />
              </div>
            </div>
            <div className="w-24 shrink-0">
              <Label className="mb-1 block text-[10px] uppercase tracking-wide text-zinc-500">Pickup</Label>
              <TimeSelect value={d.meet_time} onChange={(v) => setPickup(i, v)} testid={`${testid}-pickup-${i}`} className="w-full border-zinc-800 bg-zinc-900 focus:ring-zinc-500" />
            </div>
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2">
            <div>
              <Label className="mb-1 block text-[10px] uppercase tracking-wide text-zinc-500">Tour Hours</Label>
              <Input
                type="number"
                step="0.25"
                value={dayTourHours(d) ?? ""}
                onChange={(e) => set(i, { hours: e.target.value === "" ? 0 : Number(e.target.value), hours_manual: true })}
                className="h-10 border-zinc-800 bg-zinc-900 text-sm focus-visible:ring-zinc-500"
                data-testid={`${testid}-hours-${i}`}
              />
            </div>
            <div>
              <Label className="mb-1 block text-[10px] uppercase tracking-wide text-zinc-500">Comp</Label>
              <Input
                type="number"
                step="0.25"
                value={d.comp ?? ""}
                onChange={(e) => set(i, { comp: e.target.value === "" ? 0 : Number(e.target.value) })}
                className="h-10 border-zinc-800 bg-zinc-900 text-sm focus-visible:ring-zinc-500"
                data-testid={`${testid}-comp-${i}`}
              />
            </div>
            <div>
              <Label className="mb-1 block text-[10px] uppercase tracking-wide text-zinc-500">Rate $/hr</Label>
              {(() => {
                const hasRate = d && d.rate !== undefined && d.rate !== null && d.rate !== "" && Number(d.rate) > 0;
                const r = hasRate ? Number(d.rate) : null;
                const opts = (r != null && !RATE_OPTIONS.includes(r)) ? [...RATE_OPTIONS, r].sort((a, b) => a - b) : RATE_OPTIONS;
                return (
                  <Select value={hasRate ? String(r) : undefined} onValueChange={(v) => set(i, { rate: Number(v) })}>
                    <SelectTrigger className="h-10 border-zinc-800 bg-zinc-900 text-sm focus:ring-zinc-500" data-testid={`${testid}-rate-${i}`}>
                      <SelectValue placeholder="Set rate" />
                    </SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-950">
                      {opts.map((o) => (
                        <SelectItem key={o} value={String(o)} data-testid={`${testid}-rate-${i}-opt-${o}`}>${o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
          </div>

          {onToggleDay && multiDay && (() => {
            const done = loggedSet.has(normDate(d.date));
            const busy = busyDate === d.date;
            return (
              <button
                type="button"
                onClick={() => toggleDay(d.date)}
                disabled={busy}
                data-testid={`${testid}-complete-${i}`}
                className={`mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-600 transition-colors ${done ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-300" : "border-emerald-700/40 bg-emerald-500/[0.06] text-emerald-400 hover:bg-emerald-500/10"}`}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                {busy ? "Saving…" : done ? "Day complete — reminders off (tap to reopen)" : "Save & mark this day complete"}
              </button>
            );
          })()}
        </div>
      ))}
    </div>
  );
};
